import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from './ui/button';
import { Download, X, Smartphone, Share } from 'lucide-react';

export default function PWAInstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isIOS, setIsIOS] = useState(false);

  useEffect(() => {
    // Check if already installed (standalone mode)
    if (window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true) {
      setIsInstalled(true);
      return;
    }

    // Detect iOS
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(iOS);

    // Check if dismissed recently
    const dismissKey = iOS ? 'pwa-prompt-dismissed-ios' : 'pwa-prompt-dismissed';
    const dismissed = localStorage.getItem(dismissKey);
    if (dismissed) {
      const dismissedDate = new Date(dismissed);
      const now = new Date();
      const daysSinceDismissed = (now - dismissedDate) / (1000 * 60 * 60 * 24);
      if (daysSinceDismissed < 7) return;
    }

    // For Android/Chrome - listen for install prompt
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setTimeout(() => setShowPrompt(true), 3000);
    };

    window.addEventListener('beforeinstallprompt', handler);
    
    // Listen for successful install
    window.addEventListener('appinstalled', () => {
      setIsInstalled(true);
      setShowPrompt(false);
      setDeferredPrompt(null);
    });

    // For iOS - show custom prompt after delay
    if (iOS) {
      setTimeout(() => setShowPrompt(true), 5000);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
    };
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    
    if (outcome === 'accepted') {
      setIsInstalled(true);
    }
    
    setDeferredPrompt(null);
    setShowPrompt(false);
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    const dismissKey = isIOS ? 'pwa-prompt-dismissed-ios' : 'pwa-prompt-dismissed';
    localStorage.setItem(dismissKey, new Date().toISOString());
  };

  if (isInstalled || !showPrompt) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 50 }}
        className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-96 z-50"
      >
        <div className="bg-white rounded-2xl shadow-lg border border-border overflow-hidden">
          {/* Header */}
          <div className="p-4 bg-gradient-to-r from-primary/10 to-primary/5 flex items-start gap-3">
            <div className="w-12 h-12 rounded-xl bg-primary flex items-center justify-center flex-shrink-0">
              <Smartphone className="w-6 h-6 text-white" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-[#1A1A1A]">Install Vitala</h3>
              <p className="text-sm text-muted-foreground">
                Add to home screen for quick access
              </p>
            </div>
            <button
              onClick={handleDismiss}
              className="p-1.5 hover:bg-black/5 rounded-full transition-colors"
              data-testid="pwa-dismiss-btn"
            >
              <X className="w-4 h-4 text-muted-foreground" />
            </button>
          </div>

          {/* Content */}
          <div className="p-4">
            {isIOS ? (
              // iOS Instructions
              <div className="space-y-3">
                <p className="text-sm text-muted-foreground">
                  Install Vitala for offline access to your schedules:
                </p>
                <ol className="text-sm space-y-2">
                  <li className="flex items-center gap-2">
                    <span className="w-5 h-5 rounded-full bg-primary/10 text-primary text-xs flex items-center justify-center font-medium">1</span>
                    <span className="flex items-center gap-1">
                      Tap <Share className="w-4 h-4 text-blue-500" /> <strong>Share</strong>
                    </span>
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-5 h-5 rounded-full bg-primary/10 text-primary text-xs flex items-center justify-center font-medium">2</span>
                    <span>Tap <strong>"Add to Home Screen"</strong></span>
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-5 h-5 rounded-full bg-primary/10 text-primary text-xs flex items-center justify-center font-medium">3</span>
                    <span>Tap <strong>"Add"</strong></span>
                  </li>
                </ol>
                <Button
                  onClick={handleDismiss}
                  variant="outline"
                  className="w-full"
                  size="sm"
                >
                  Got it
                </Button>
              </div>
            ) : (
              // Android/Chrome Install
              <div className="space-y-3">
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-primary" />
                    Quick access from home screen
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-primary" />
                    View schedules offline
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-primary" />
                    Push notification reminders
                  </li>
                </ul>
                <div className="flex gap-2">
                  <Button
                    onClick={handleDismiss}
                    variant="outline"
                    size="sm"
                    className="flex-1"
                  >
                    Not now
                  </Button>
                  <Button
                    onClick={handleInstall}
                    className="btn-primary flex-1"
                    size="sm"
                    data-testid="pwa-install-btn"
                  >
                    <Download className="w-4 h-4 mr-1" />
                    Install
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
